import { createClient } from "@connectrpc/connect";
import { SYNC_KEY_SEGURIDAD_USUARIOS_APP } from "../../../constantes";
import {
  CuentaAppItem,
  UsuariosAppPlataformaService,
} from "../../generated/grpc/plataforma_seguridad_usuarios_app_pb";
import { UsuarioAppResponse } from "../../response/seguridad/UsuarioAppResponse";
import { ServiceGetListBase } from "../base/ServiceGetListBase";
import { grpcTransportProvider } from "../grpc/GrpcTransport";
import { mapConnectError } from "../grpc/mapConnectError";

/**
 * Cuentas de la app web de un cliente (`UsuariosAppPlataformaService`, gRPC-Web): lista en
 * memoria por cliente y las acciones activar / inactivar / restablecer contraseña.
 */
export class UsuarioAppListByClienteIdService extends ServiceGetListBase<UsuarioAppResponse> {
  private static readonly _instances = new Map<string, UsuarioAppListByClienteIdService>();

  /** Singleton por cliente: cada detalle de cliente tiene su propia lista de cuentas. */
  static instance(clienteId: string): UsuarioAppListByClienteIdService {
    if (!this._instances.has(clienteId))
      this._instances.set(clienteId, new UsuarioAppListByClienteIdService(clienteId));
    return this._instances.get(clienteId)!;
  }

  /** Clave única del listado: deduplica `sync()` (dos pedidos simultáneos, un solo request). */
  protected readonly syncKey: string;

  /** Guarda el cliente dueño de las cuentas y arma la clave de deduplicación. */
  private constructor(private readonly _clienteId: string) {
    super();
    this.syncKey = `${SYNC_KEY_SEGURIDAD_USUARIOS_APP}/byClienteId/${_clienteId}`;
  }

  /** gRPC-Web (`ListByCliente`): cuentas no eliminadas, las que administran usuarios primero. */
  protected async fetchItems(): Promise<UsuarioAppResponse[]> {
    try {
      const response = await this._client().listByCliente({ clienteId: this._clienteId });
      return response.items.map(toUsuarioAppResponse);
    } catch (error) {
      throw mapConnectError(error);
    }
  }

  // ── Acciones ────────────────────────────────────────────────────────────────
  // No devuelven datos: quien llama vuelve a sincronizar la lista.

  /** Reactiva la cuenta. No cierra sesiones. */
  activar(id: string): Promise<void> {
    return this._accion(() => this._client().activar({ id }));
  }

  /** Suspende la cuenta y cierra sus sesiones abiertas. */
  inactivar(id: string): Promise<void> {
    return this._accion(() => this._client().inactivar({ id }));
  }

  /** El servidor genera la contraseña nueva, cierra las sesiones y la envía por correo; nunca la devuelve. */
  restablecerPassword(id: string): Promise<void> {
    return this._accion(() => this._client().restablecerPassword({ id }));
  }

  /** Cliente gRPC del servicio de cuentas sobre el transporte único de la app. */
  private _client() {
    return createClient(UsuariosAppPlataformaService, grpcTransportProvider.get());
  }

  /** Ejecuta una acción y traduce su error a la jerarquía de errores de la app. */
  private async _accion(llamada: () => Promise<unknown>): Promise<void> {
    try {
      await llamada();
    } catch (error) {
      throw mapConnectError(error);
    }
  }
}

/** Mensaje gRPC de la cuenta → `UsuarioAppResponse`. Lo reusa el guardado. */
export function toUsuarioAppResponse(item: CuentaAppItem): UsuarioAppResponse {
  return {
    id: item.id,
    isEliminado: false,
    nombre: item.nombre,
    email: item.email,
    isActivo: item.isActivo,
    hasUsuarios: item.hasUsuarios,
  };
}
